/**
 * PageHeader — the canonical title block at the top of a page.
 *
 * Replaces the various inline `<h1 className="text-3xl">…<p>` patterns
 * scattered across pages so typography stays consistent with the topbar
 * + sidebar density.
 *
 * Usage:
 *   <PageHeader
 *     title={t('nav.cases')}
 *     subtitle="Manage your cases and matters"
 *     icon={HiOutlineFolder}
 *     actions={<Button leadingIcon={HiOutlinePlus}>New case</Button>}
 *   />
 *
 * Optional `eyebrow` slot for breadcrumb/context above the title.
 */

export default function PageHeader({
  title,
  subtitle,
  icon: Icon,
  iconTone = 'brand',
  eyebrow,
  actions,
  className = '',
}) {
  const ICON_TONE = {
    brand:   'bg-brand-soft text-brand',
    success: 'bg-success-soft text-success',
    info:    'bg-info-soft text-info',
    warning: 'bg-warning-soft text-warning',
    neutral: 'bg-muted text-ink-secondary',
  }
  return (
    <header className={`mb-5 sm:mb-6 ${className}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          {Icon && (
            <span className={`inline-flex items-center justify-center w-10 h-10 rounded-xl flex-shrink-0 ${ICON_TONE[iconTone] || ICON_TONE.brand}`}>
              <Icon className="w-5 h-5" aria-hidden="true" />
            </span>
          )}
          <div className="min-w-0">
            {eyebrow && (
              <div className="text-[11px] font-medium uppercase tracking-wider text-ink-muted mb-1">{eyebrow}</div>
            )}
            <h1 className="text-xl sm:text-2xl font-semibold text-ink-primary leading-tight truncate">{title}</h1>
            {subtitle && (
              <p className="text-sm text-ink-muted mt-1 line-clamp-2">{subtitle}</p>
            )}
          </div>
        </div>
        {actions && (
          <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
            {actions}
          </div>
        )}
      </div>
    </header>
  )
}
